import React, { useCallback, useState } from 'react';
import type { FaseOS, OrdemServico } from '../types';
import { useOSStore } from '../stores/osStore';
import { useAuthStore } from '../stores/authStore';
import { useTrackingStore } from '../stores/trackingStore';
import { openExternalNavigation } from '../utils/openNavigation';
import { abrirAppNativoComDeteccao, openNativeApp } from '../utils/nativeApp';
import {
  getMensagemBloqueioInicio,
  isSepultamentoMesmoLocalInformativo,
  podeCancelarFase,
  podeFinalizarFase,
  podeIniciarFase,
} from '../utils/faseRules';
import QrScannerModal from './QrScannerModal';

interface FaseCardProps {
  fase: FaseOS;
  ordemServico: OrdemServico;
}

const normalizeNota = (value: string | undefined | null): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, '');

/** O app nativo só faz rastreamento em segundo plano no Android. */
const isAndroid = (): boolean => /Android/i.test(navigator.userAgent);

/** Garante permissão de localização do navegador (abre o pedido do sistema se ainda não foi respondido). */
const garantirPermissaoLocalizacao = async (): Promise<boolean> => {
  if (!navigator.geolocation) return false;
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    if (status?.state === 'granted') return true;
    if (status?.state === 'denied') return false;
  } catch {
    /* Permissions API indisponível: segue para o pedido direto */
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      (error) => resolve(error.code !== error.PERMISSION_DENIED),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  });
};

const GoogleMapsIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z" />
    <circle fill="#FFFFFF" cx="12" cy="9" r="2.5" />
    <path fill="#34A853" d="M12 2c-1.2 0-2.3.3-3.3.9L12 9l3.3-6.1C14.3 2.3 13.2 2 12 2z" opacity=".35" />
    <path fill="#FBBC05" d="M5.5 12.5C6.8 16.2 12 22 12 22s.8-.9 2-2.5L8 9.5 5.5 12.5z" opacity=".5" />
    <path fill="#EA4335" d="M18.5 12.5C17.2 16.2 12 22 12 22s-.8-.9-2-2.5L16 9.5l2.5 3z" opacity=".5" />
  </svg>
);

const WazeIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#33CCFF"
      d="M12 2C7.6 2 4 5.4 4 9.5c0 2.6 1.3 4.9 3.3 6.4L6 21l4.2-2.1c.6.1 1.2.2 1.8.2 4.4 0 8-3.4 8-7.6S16.4 2 12 2z"
    />
    <circle fill="#1A1A1A" cx="9.2" cy="9.2" r="1.3" />
    <circle fill="#1A1A1A" cx="14.8" cy="9.2" r="1.3" />
    <path
      fill="none"
      stroke="#1A1A1A"
      strokeWidth="1.4"
      strokeLinecap="round"
      d="M8.5 12.5c1 .9 2.2 1.4 3.5 1.4s2.5-.5 3.5-1.4"
    />
  </svg>
);

const FaseCard: React.FC<FaseCardProps> = ({ fase, ordemServico }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [ofertaAppNativo, setOfertaAppNativo] = useState(false);
  const { updateFaseStatus, cancelarFase, error: storeError, clearError } = useOSStore();
  const { token } = useAuthStore();
  const {
    startTracking,
    stopTracking,
    activeTargets,
    nativeTargets,
    trackingError,
    delegarAoAppNativo,
    removerDelegacaoNativa,
  } = useTrackingStore();

  const tituloOS = ordemServico.numero_nota_contratacao
    ? `N.C. ${ordemServico.numero_nota_contratacao}`
    : `OS #${ordemServico.codigo_acesso}`;

  const isInformativoMesmoLocal = isSepultamentoMesmoLocalInformativo(fase, ordemServico);

  const mensagemBloqueio = isInformativoMesmoLocal
    ? null
    : getMensagemBloqueioInicio(fase, ordemServico.fases);
  const canStart = !isInformativoMesmoLocal && podeIniciarFase(fase, ordemServico.fases);
  const canFinish = !isInformativoMesmoLocal && podeFinalizarFase(fase);
  const canCancel = !isInformativoMesmoLocal && podeCancelarFase(fase);
  const isThisTargetActive = activeTargets.some(
    (t) => t.osId === ordemServico.id && t.faseId === fase.id
  );
  const isNativeTarget = nativeTargets.some(
    (t) => t.osId === ordemServico.id && t.faseId === fase.id
  );

  const handleOpenNav = (app: 'google' | 'waze') => {
    const result = openExternalNavigation(app, fase);
    if (!result.ok) {
      setLocalError(result.error);
    }
  };

  const handleAbrirScanner = () => {
    if (!canStart || isProcessing) return;
    setLocalError(null);
    setAviso(null);
    setScanError(null);
    clearError();
    setScannerOpen(true);
  };

  const handleFecharScanner = () => {
    if (isProcessing) return;
    setScannerOpen(false);
    setScanError(null);
  };

  const iniciarComQr = useCallback(
    async (qrCode: string) => {
      if (isProcessing) return;

      const notaEsperada = normalizeNota(ordemServico.numero_nota_contratacao);
      const qrLido = normalizeNota(qrCode);

      if (!notaEsperada) {
        setScanError(
          'Esta OS não possui Nota de Contratação cadastrada. Contate o suporte.'
        );
        return;
      }

      if (!qrLido || qrLido !== notaEsperada) {
        setScanError(
          'QR Code não confere com a Nota de Contratação deste serviço. Tente novamente.'
        );
        return;
      }

      setIsProcessing(true);
      setScanError(null);
      setLocalError(null);
      clearError();

      try {
        const permitido = await garantirPermissaoLocalizacao();
        if (!permitido) {
          setScannerOpen(false);
          setLocalError(
            'É necessário permitir localização para o rastreamento. Ative a localização do navegador para este site.'
          );
          return;
        }

        await updateFaseStatus(fase.id, 'Em Rota', { qr_code: qrLido });
        startTracking(ordemServico.id, fase.id);
        setScannerOpen(false);
        if (token && isAndroid()) {
          setOfertaAppNativo(true);
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : 'Não foi possível iniciar. Tente novamente.';
        setScanError(message);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      clearError,
      fase.id,
      isProcessing,
      ordemServico.id,
      ordemServico.numero_nota_contratacao,
      startTracking,
      token,
      updateFaseStatus,
    ]
  );

  const handleUsarAppNativo = async () => {
    if (!token) return;
    setOfertaAppNativo(false);
    setAviso(null);
    delegarAoAppNativo(ordemServico.id, fase.id);
    const abriu = await abrirAppNativoComDeteccao({
      osId: ordemServico.id,
      faseId: fase.id,
      token,
      action: 'start',
    });
    if (!abriu) {
      removerDelegacaoNativa(ordemServico.id, fase.id);
      startTracking(ordemServico.id, fase.id);
      setAviso(
        'Não foi possível abrir o app nativo. O rastreamento continua pelo navegador; mantenha esta tela aberta.'
      );
    }
  };

  const encerrarRastreamento = () => {
    stopTracking(ordemServico.id, fase.id);
    if (isNativeTarget) {
      if (token) {
        openNativeApp({ osId: ordemServico.id, faseId: fase.id, token, action: 'stop' });
      }
      removerDelegacaoNativa(ordemServico.id, fase.id);
    }
  };

  const handleFinalizarProcesso = async () => {
    if (!canFinish || isProcessing) return;
    const confirmed = window.confirm(
      'Deseja finalizar o processo desta fase? O rastreamento será encerrado.'
    );
    if (!confirmed) return;
    setIsProcessing(true);
    setLocalError(null);
    setAviso(null);
    setOfertaAppNativo(false);
    clearError();
    try {
      encerrarRastreamento();
      await updateFaseStatus(fase.id, 'Concluída');
    } catch {
      setLocalError('Não foi possível finalizar. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelarProcesso = async () => {
    if (!canCancel || isProcessing) return;
    const confirmed = window.confirm(
      'Deseja cancelar esta fase? O rastreamento será encerrado e a fase voltará para Pendente.'
    );
    if (!confirmed) return;
    setIsProcessing(true);
    setLocalError(null);
    setAviso(null);
    setOfertaAppNativo(false);
    clearError();
    try {
      encerrarRastreamento();
      await cancelarFase(fase.id);
    } catch {
      setLocalError('Não foi possível cancelar. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isInformativoMesmoLocal) {
    return (
      <div className="card p-4 border border-purple-200 bg-purple-50">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{fase.nome_fase}</h3>
            <p className="text-sm text-gray-500">{tituloOS}</p>
          </div>
          <span className="chip chip-gray">ℹ️ Informativo</span>
        </div>
        <p className="text-sm text-purple-900 mb-2">
          Sepultamento no mesmo local do velório. Não é necessário deslocamento nem execução desta fase.
        </p>
        {(fase.data_fase || fase.horario) && (
          <p className="text-xs text-gray-600">
            Horário previsto do sepultamento:{' '}
            {fase.data_fase && new Date(fase.data_fase).toLocaleDateString('pt-BR')}
            {fase.horario && ` • ${fase.horario}`}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`card p-4 ${mensagemBloqueio ? 'opacity-80' : ''}`}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{fase.nome_fase}</h3>
            <p className="text-sm text-gray-500">{tituloOS}</p>
          </div>
          <span className={`chip ${
            fase.status_fase === 'Em Rota'
              ? 'chip-blue'
              : fase.status_fase === 'Pendente'
                ? 'chip-yellow'
                : 'chip-green'
          }`}>
            {fase.status_fase === 'Em Rota' ? '🚗' : fase.status_fase === 'Pendente' ? '⏳' : '✅'} {fase.status_fase}
          </span>
        </div>
        <div className="text-sm text-gray-600 mb-4">
          <p className="font-medium text-gray-700 mb-1">📍 Destino</p>
          <p className="text-gray-500">{fase.endereco_destino}</p>
        </div>
        {(fase.data_fase || fase.horario) && (
          <p className="text-xs text-gray-500 mb-4">
            {fase.data_fase &&
              new Date(fase.data_fase).toLocaleDateString('pt-BR')}
            {fase.horario && ` • ${fase.horario}`}
          </p>
        )}

        {(localError || storeError) && (
          <div className="error-box mb-4 text-sm">{localError || storeError}</div>
        )}

        {isThisTargetActive && trackingError && (
          <div className="error-box mb-4 text-sm">{trackingError}</div>
        )}

        {aviso && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {aviso}
          </div>
        )}

        {mensagemBloqueio && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            🔒 {mensagemBloqueio}
          </div>
        )}

        {ofertaAppNativo && fase.status_fase === 'Em Rota' && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-900">
            <p className="font-medium mb-1">📱 Rastrear pelo app nativo?</p>
            <p className="text-xs mb-3">
              Pelo navegador, a localização só é enviada com esta tela aberta. O app nativo
              continua rastreando com a tela desligada ou usando o Maps/Waze.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn-primary btn-icon flex-1"
                onClick={handleUsarAppNativo}
              >
                Usar app nativo
              </button>
              <button
                type="button"
                className="btn-secondary btn-icon flex-1"
                onClick={() => setOfertaAppNativo(false)}
              >
                Continuar no navegador
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            className="btn-secondary btn-icon flex-1"
            onClick={() => handleOpenNav('google')}
            title="Abrir no Google Maps"
            aria-label="Abrir destino no Google Maps"
          >
            <span className="inline-flex items-center justify-center gap-2">
              <GoogleMapsIcon />
              <span>Maps</span>
            </span>
          </button>
          <button
            type="button"
            className="btn-secondary btn-icon flex-1"
            onClick={() => handleOpenNav('waze')}
            title="Abrir no Waze"
            aria-label="Abrir destino no Waze"
          >
            <span className="inline-flex items-center justify-center gap-2">
              <WazeIcon />
              <span>Waze</span>
            </span>
          </button>

          {isThisTargetActive && (
            <span className="chip chip-green">📡 Rastreamento ativo</span>
          )}
          {isNativeTarget && (
            <span className="chip chip-green">📱 Rastreando pelo app nativo</span>
          )}
          {canStart && (
            <button
              type="button"
              className="btn-primary btn-icon flex-1"
              onClick={handleAbrirScanner}
              disabled={isProcessing}
            >
              {isProcessing ? 'Aguarde...' : '▶️ Iniciar'}
            </button>
          )}
          {canFinish && (
            <button
              type="button"
              className="btn-secondary btn-icon flex-1"
              onClick={handleFinalizarProcesso}
              disabled={isProcessing}
            >
              {isProcessing ? 'Aguarde...' : '⏹️ Finalizar'}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              className="btn-secondary btn-icon flex-1 border-red-200 text-red-700"
              onClick={handleCancelarProcesso}
              disabled={isProcessing}
            >
              {isProcessing ? 'Aguarde...' : '↩️ Cancelar'}
            </button>
          )}
        </div>
      </div>

      <QrScannerModal
        open={scannerOpen}
        titulo={`Validar etiqueta — ${fase.nome_fase}`}
        scanError={scanError}
        onClose={handleFecharScanner}
        onScan={iniciarComQr}
        onClearScanError={() => setScanError(null)}
      />
    </>
  );
};

export default FaseCard;
